import { AxiosError } from "axios";
import { getApiErrorMessage } from "@/shared/lib/api-error";

const TECHNICAL_PREFIXES = [
  "Erro de validação:",
  "Erro inesperado:",
  "Requisição inválida:",
  "Recurso não encontrado:",
  "Erro no servidor Focus NFe:",
  "Rejeicao:",
  "Rejeição:",
];

const MAX_MESSAGE_LENGTH = 160;

export interface ReadableSefazMessage {
  message: string;
  code: string | null;
}

interface FocusErrorPayload {
  mensagem_sefaz?: unknown;
  status_sefaz?: unknown;
  mensagem?: unknown;
  message?: unknown;
  erros?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asText(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function parseEmbeddedJson(raw: string): FocusErrorPayload | null {
  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");
  if (start === -1 || end <= start) return null;
  try {
    const parsed: unknown = JSON.parse(raw.slice(start, end + 1));
    return isRecord(parsed) ? (parsed as FocusErrorPayload) : null;
  } catch {
    return null;
  }
}

function firstErrorMessage(erros: unknown): string | null {
  if (!Array.isArray(erros)) return null;
  for (const item of erros) {
    if (!isRecord(item)) continue;
    const message = asText(item.mensagem) ?? asText(item.message);
    if (message) return message;
  }
  return null;
}

function stripTechnicalPrefixes(raw: string): string {
  let text = raw.trim();
  let changed = true;
  while (changed) {
    changed = false;
    for (const prefix of TECHNICAL_PREFIXES) {
      if (text.toLowerCase().startsWith(prefix.toLowerCase())) {
        text = text.slice(prefix.length).trim();
        changed = true;
      }
    }
  }
  return text;
}

function truncate(text: string): string {
  if (text.length <= MAX_MESSAGE_LENGTH) return text;
  return `${text.slice(0, MAX_MESSAGE_LENGTH).trimEnd()}…`;
}

export function parseSefazMessage(
  raw: string | null | undefined,
): ReadableSefazMessage | null {
  if (!raw || !raw.trim()) return null;

  const payload = parseEmbeddedJson(raw);
  const fromPayload = payload
    ? (asText(payload.mensagem_sefaz) ??
      firstErrorMessage(payload.erros) ??
      asText(payload.mensagem) ??
      asText(payload.message))
    : null;

  const code = payload ? asText(payload.status_sefaz) : null;
  const text = stripTechnicalPrefixes(fromPayload ?? raw);

  if (!text) return null;
  if (text.startsWith("{")) {
    return { message: "A SEFAZ recusou a nota sem detalhar o motivo.", code };
  }

  return { message: truncate(stripTechnicalPrefixes(text)), code };
}

export function getReadableErrorMessage(
  error: unknown,
  fallback: string,
): string {
  const raw = getApiErrorMessage(error, fallback);
  return parseSefazMessage(raw)?.message ?? fallback;
}

export function isUnconfirmedRequest(error: unknown): boolean {
  return (
    error instanceof AxiosError &&
    !error.response &&
    error.code !== "ERR_CANCELED"
  );
}
